import { useEffect } from "react";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import "@/App.css";

import { Nav } from "@/components/Nav";
import { Hero } from "@/components/Hero";
import { Marquee } from "@/components/Marquee";
import { WhatIsAiga } from "@/components/WhatIsAiga";
import { AidFeature } from "@/components/AidFeature";
import { Problem } from "@/components/Problem";
import { MeaningBehindName } from "@/components/MeaningBehindName";
import { AidScrollExperience } from "@/components/AidScrollExperience";
import { SharingFlow } from "@/components/SharingFlow";
import { AigaCommunity } from "@/components/AigaCommunity";
import { Founder } from "@/components/Founder";
import { RegisterInterest } from "@/components/RegisterInterest";
import { Footer } from "@/components/Footer";
import { AdminInbox } from "@/pages/AdminInbox";
import ExampleAid from "@/pages/ExampleAid";
import { AccessibilityMenu } from "@/components/AccessibilityMenu";

const Home = () => {
  useEffect(() => {
    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) return;

    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  // Subtle scroll-based section focus: the section under the viewport's centre
  // line gets full emphasis; others ease to slightly quieter (opacity + gentle
  // brightness). Continuous and understated — no snapping. Off for reduced motion.
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const sections = Array.from(document.querySelectorAll("#main section"));
    if (!sections.length) return;

    sections.forEach((s) => {
      s.style.transition = "opacity 0.4s ease, filter 0.4s ease";
      s.style.willChange = "opacity";
    });

    const FALLOFF = 380; // px past a section's edge before it reaches the quietest state
    const MIN_OPACITY = 0.6;
    const MIN_BRIGHT = 0.86;

    let raf = 0;
    const update = () => {
      raf = 0;
      const focus = window.innerHeight / 2;
      for (const s of sections) {
        const r = s.getBoundingClientRect();
        let e;
        if (focus >= r.top && focus <= r.bottom) e = 1;
        else {
          const d = focus < r.top ? r.top - focus : focus - r.bottom;
          e = Math.max(0, 1 - d / FALLOFF);
        }
        s.style.opacity = (MIN_OPACITY + (1 - MIN_OPACITY) * e).toFixed(3);
        s.style.filter = `brightness(${(MIN_BRIGHT + (1 - MIN_BRIGHT) * e).toFixed(3)})`;
      }
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      cancelAnimationFrame(raf);
      sections.forEach((s) => {
        s.style.opacity = "";
        s.style.filter = "";
        s.style.transition = "";
        s.style.willChange = "";
      });
    };
  }, []);

  return (
    <div className="App min-h-screen bg-navy-900 text-slatelight selection:bg-cyan/30">
      <a href="#top" className="skip-link">Skip to main content</a>
      <Nav />
      <main id="main">
        <Hero />
        <Marquee />

        {/* 01 — Aiga Enable: the company, vision and the problem */}
        <div className="ia-area ia-area--tint">
          <WhatIsAiga />
          <Problem />
        </div>

        {/* 02 — A-ID: the product and what it does */}
        <div className="ia-area">
          <AidFeature />
        </div>

        {/* 03 — How A-ID Works */}
        <div className="ia-area ia-area--tint">
          <AidScrollExperience />
          <SharingFlow />
        </div>

        {/* 04 — Aiga Community */}
        <div className="ia-area">
          <AigaCommunity />
        </div>

        {/* 05 — Our Story: the meaning behind the name and the founder */}
        <div className="ia-area ia-area--tint">
          <MeaningBehindName />
          <Founder />
        </div>

        {/* 06 — Early Access */}
        <div className="ia-area">
          <RegisterInterest />
        </div>
      </main>
      <Footer />
    </div>
  );
};

function App() {
  return (
    <div className="bg-navy-900 min-h-screen text-slatelight">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/aiga-inbox" element={<AdminInbox />} />
          <Route path="/example-aid" element={<ExampleAid />} />
        </Routes>
        <AccessibilityMenu />
      </BrowserRouter>
      <Toaster
        theme="dark"
        position="bottom-center"
        richColors
        toastOptions={{
          style: { background: "#0E1C33", border: "1px solid rgba(255,255,255,0.1)", color: "#E2E8F0" },
        }}
      />
    </div>
  );
}

export default App;
